import React, { useEffect, useState } from 'react';
import { i18n } from '../../i18n/i18n';
import { useDarkMode } from '../../context/DarkModeContext';
import { useLanguage } from '../../context/LanguageContext';
import { supported_languages } from '../../i18n/translations';
import { THEME_ICON_PATHS } from '../../constants/navIcons';
import { getNotificationPrefs, ParentControl } from '../../services/apiService';
import { useDevicePush } from '../../hooks/useDevicePush';
import { KidButton, KidCard } from './KidUI';
import { useAppContext } from '../../context/AppContext';
import { TIMER_SOUNDS, TimerSound, getTimerSound, playTimerSound, setTimerSound, unlockTimerAudio } from '../../utils/timerSounds';

/**
 * A child's own choices (redesign step 6c): language, light/dark, and notifications on this
 * device — unless a grown-up decides those (notification_prefs.parent_control). Also the sound a
 * step timer makes at zero (2026-10-19), per person on this device.
 */

const chip = (on: boolean) =>
  `min-h-[48px] px-4 rounded-full border-2 font-bold text-base inline-flex items-center gap-2 ${
    on
      ? 'bg-kid-green-bg dark:bg-green-900/40 border-kid-green text-kid-green-shadow dark:text-green-200'
      : 'bg-kid-card dark:bg-kid-card-dark border-kid-border-strong dark:border-kid-border-strong-dark'
  }`;

const Heading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="font-kid-heading font-extrabold text-xl mb-3">{children}</h3>
);

const MySettings: React.FC = () => {
  const { isDarkMode, setDarkMode, isSystemPreference, setSystemPreference } = useDarkMode();
  const { current_language, set_language, use_system_language, set_use_system_language } = useLanguage();
  const [control, setControl] = useState<ParentControl | null>(null);
  const { device, busy, turnOn, turnOff } = useDevicePush();
  const { current_user } = useAppContext() as any;
  const [timerSound, setTimerSoundState] = useState<TimerSound>(() => getTimerSound(current_user?.id));
  const chooseTimerSound = (sound: TimerSound) => {
    unlockTimerAudio();
    setTimerSound(current_user?.id, sound);
    setTimerSoundState(sound);
    playTimerSound(sound); // hear it straight away
  };

  useEffect(() => {
    let alive = true;
    getNotificationPrefs()
      .then((p) => { if (alive) setControl((p?.parent_control as ParentControl) || 'child'); })
      .catch(() => { if (alive) setControl('child'); });
    return () => { alive = false; };
  }, []);

  const languages = [...supported_languages].sort((a, b) => a.label.localeCompare(b.label));

  const notifications = () => {
    if (control === null || device === null) return <p className="text-base">{i18n.t('common.loading')}</p>;
    if (control === 'off') return <p className="text-base font-bold">{i18n.t('my_settings.notif_parent_off')}</p>;
    if (device === 'unsupported') return <p className="text-base">{i18n.t('my_settings.notif_unsupported')}</p>;
    if (device === 'blocked') return <p className="text-base">{i18n.t('my_settings.notif_blocked')}</p>;
    if (control === 'on') {
      return (
        <div className="space-y-3">
          <p className="text-base font-bold">{i18n.t('my_settings.notif_parent_on')}</p>
          {device === 'off' && (
            <KidButton size="md" disabled={busy} onClick={turnOn}>{i18n.t('my_settings.notif_turn_on')}</KidButton>
          )}
        </div>
      );
    }
    return (
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={i18n.t('my_settings.notifications')}>
        <button type="button" role="radio" aria-checked={device === 'on'} disabled={busy} className={chip(device === 'on')}
          onClick={() => device !== 'on' && turnOn()}>
          🔔 {i18n.t('my_settings.on')}
        </button>
        <button type="button" role="radio" aria-checked={device === 'off'} disabled={busy} className={chip(device === 'off')}
          onClick={() => device !== 'off' && turnOff()}>
          🔕 {i18n.t('my_settings.off')}
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-4 text-kid-text dark:text-kid-text-dark">
      <KidCard>
        <Heading>🌐 {i18n.t('my_settings.language')}</Heading>
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={i18n.t('my_settings.language')}>
          <button type="button" role="radio" aria-checked={use_system_language} className={chip(use_system_language)}
            onClick={() => set_use_system_language(true)}>
            {i18n.t('my_settings.same_as_device')}
          </button>
          {languages.map((l) => {
            const on = !use_system_language && current_language === l.code;
            return (
              <button key={l.code} type="button" role="radio" aria-checked={on} lang={l.code} className={chip(on)}
                onClick={() => { set_use_system_language(false); set_language(l.code); }}>
                {l.label}
              </button>
            );
          })}
        </div>
      </KidCard>

      <KidCard>
        <Heading>{i18n.t('my_settings.look')}</Heading>
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={i18n.t('my_settings.look')}>
          <button type="button" role="radio" aria-checked={!isSystemPreference && !isDarkMode} className={chip(!isSystemPreference && !isDarkMode)}
            onClick={() => setDarkMode(false)}>
            <img src={THEME_ICON_PATHS.light} alt="" aria-hidden className="h-6 w-6 object-contain" /> {i18n.t('my_settings.light')}
          </button>
          <button type="button" role="radio" aria-checked={!isSystemPreference && isDarkMode} className={chip(!isSystemPreference && isDarkMode)}
            onClick={() => setDarkMode(true)}>
            <img src={THEME_ICON_PATHS.dark} alt="" aria-hidden className="h-6 w-6 object-contain" /> {i18n.t('my_settings.dark')}
          </button>
          <button type="button" role="radio" aria-checked={isSystemPreference} className={chip(isSystemPreference)}
            onClick={() => setSystemPreference(true)}>
            {i18n.t('my_settings.same_as_device')}
          </button>
        </div>
      </KidCard>

      <KidCard>
        <Heading>⏱ {i18n.t('my_settings.timer_sound')}</Heading>
        <p className="text-base mb-3">{i18n.t('my_settings.timer_sound_help')}</p>
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={i18n.t('my_settings.timer_sound')}>
          {TIMER_SOUNDS.map((sound) => (
            <button key={sound} type="button" role="radio" aria-checked={timerSound === sound} className={chip(timerSound === sound)}
              onClick={() => chooseTimerSound(sound)}>
              <span aria-hidden>{timerSound === sound ? '🔊' : '▶'}</span> {i18n.t(`timer.sound_${sound}`)}
            </button>
          ))}
        </div>
      </KidCard>

      <KidCard>
        <Heading>🔔 {i18n.t('my_settings.notifications')}</Heading>
        {notifications()}
      </KidCard>
    </div>
  );
};

export default MySettings;
